/**
 * @class CardProduct
 * @extends {HTMLElement}
 * @description A custom element that represents a product card and is capable of
 * updating its content when the Variant changes.
 */
class CardProduct extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.variantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleVariantChange.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }

  /**
   * @method handleVariantChange
   */
  handleVariantChange({ data }) {
    const productFormId = data.target.dataset.formId;
    if (productFormId === this.dataset.productFormId) {
      this.fetchAndUpdateProductCard(data.selectedOptionValues);
    }
  }

  async fetchAndUpdateProductCard(optionValues) {
    const requestUrl = this.buildRequestUrlWithParams(optionValues);
    const response = await fetch(requestUrl);
    const responseText = await response.text();

    const html = new DOMParser().parseFromString(responseText, "text/html");

    ["product-form-content", "variant-price", "variant-properties"].forEach((item) => {
      const selector = `[data-${item}]`;
      const wrapper = this.querySelector(selector);
      const elementFromCard = html.querySelector(selector);
      if (wrapper && elementFromCard) {
        wrapper.innerHTML = elementFromCard.innerHTML;
      }
    });

    const variantId =
      html.querySelector("card-product").dataset.currentVariantId;

    this.updateMainImage(variantId);
  }

  buildRequestUrlWithParams(optionValues) {
    const { productHandle } = this.dataset;
    const productUrl = `/products/${productHandle}`;
    const params = [];

    params.push("section_id=card-product");

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(",")}`);
    }

    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * @method updateMainImage
   * @description Updates the visible image in the card. It hides all variant images
   * and then shows only the one that matches the provided variant ID.
   * @param {string} variantId - The ID of the Variant to display
   */
  updateMainImage(variantId) {
    const variantImageEls = [...this.querySelectorAll("[data-card-media-variant-image]")];
    const variantImage = variantImageEls.find(
      (imageEl) => imageEl.dataset.cardMediaVariantId === variantId
    );

    if (variantImage !== undefined) {
      variantImageEls.forEach(
        (imageEl) => imageEl.classList.add("hidden")
      );
      variantImage.classList.remove("hidden");
    }
  }
}

customElements.define("card-product", CardProduct);
